import * as React from 'react';

import {
  applySelectedTextUtility,
  applyTextUtilities,
  findActiveTextUtilityContext,
  getFilteredTextUtilityCommands,
} from '~/lib/text-utilities';
import { cn } from '~/lib/utils';

type TextareaProps = React.ComponentProps<'textarea'> & {
  enableTextUtilities?: boolean;
};

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, onChange, onKeyDown, onClick, onKeyUp, enableTextUtilities = true, ...props }, ref) => {
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
    const [currentValue, setCurrentValue] = React.useState<string>(String(props.value ?? props.defaultValue ?? ''));
    const [cursorPosition, setCursorPosition] = React.useState<number>(currentValue.length);
    const [activeCommandIndex, setActiveCommandIndex] = React.useState(0);

    React.useEffect(() => {
      if (typeof props.value === 'string') {
        setCurrentValue(props.value);
      }
    }, [props.value]);

    const utilityContext = React.useMemo(
      () => (enableTextUtilities ? findActiveTextUtilityContext(currentValue, cursorPosition) : null),
      [currentValue, cursorPosition, enableTextUtilities],
    );
    const filteredCommands = React.useMemo(
      () => (utilityContext ? getFilteredTextUtilityCommands(utilityContext.query) : []),
      [utilityContext],
    );
    const showUtilities = filteredCommands.length > 0 && utilityContext !== null;

    const setRefs = (node: HTMLTextAreaElement | null) => {
      textareaRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    const applyUtility = (token: string) => {
      if (!textareaRef.current || !utilityContext) return;
      const applied = applySelectedTextUtility(textareaRef.current.value, utilityContext, token);
      if (!applied) return;
      const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set;
      setter?.call(textareaRef.current, applied.nextValue);
      textareaRef.current.setSelectionRange(applied.nextCursor, applied.nextCursor);
      setCurrentValue(applied.nextValue);
      setCursorPosition(applied.nextCursor);
      setActiveCommandIndex(0);
      textareaRef.current.dispatchEvent(new Event('input', { bubbles: true }));
      textareaRef.current.focus();
    };

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (enableTextUtilities) {
        const transformed = applyTextUtilities(event.currentTarget.value);
        if (transformed !== event.currentTarget.value) {
          const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set;
          setter?.call(event.currentTarget, transformed);
        }
      }
      setCurrentValue(event.currentTarget.value);
      setCursorPosition(event.currentTarget.selectionStart ?? event.currentTarget.value.length);
      setActiveCommandIndex(0);
      onChange?.(event);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (showUtilities) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setActiveCommandIndex((prev) => (prev + 1) % filteredCommands.length);
          return;
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          setActiveCommandIndex((prev) => (prev - 1 + filteredCommands.length) % filteredCommands.length);
          return;
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          setActiveCommandIndex(0);
          return;
        }
        if (event.key === 'Enter') {
          event.preventDefault();
          const selected = filteredCommands[activeCommandIndex] ?? filteredCommands[0];
          if (selected) applyUtility(selected.token);
          return;
        }
      }
      onKeyDown?.(event);
    };

    return (
      <div className="relative w-full">
        <textarea
          className={cn(
            'flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-base placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
            className,
          )}
          ref={setRefs}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onClick={(event) => {
            setCursorPosition(event.currentTarget.selectionStart ?? event.currentTarget.value.length);
            onClick?.(event);
          }}
          onKeyUp={(event) => {
            setCursorPosition(event.currentTarget.selectionStart ?? event.currentTarget.value.length);
            onKeyUp?.(event);
          }}
          {...props}
        />
        {showUtilities ? (
          <div className="absolute left-0 right-0 top-[calc(100%+0.4rem)] z-30 rounded-lg border border-border bg-background shadow-lg overflow-hidden">
            {filteredCommands.map((command, index) => (
              <button
                key={command.token}
                type="button"
                onMouseDown={(event) => {
                  event.preventDefault();
                  applyUtility(command.token);
                }}
                className={`w-full text-left px-3 py-2 transition-colors ${
                  index === activeCommandIndex ? 'bg-muted' : 'hover:bg-muted/60'
                }`}
              >
                <p className="text-sm font-medium">{command.token}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{command.description}</p>
              </button>
            ))}
          </div>
        ) : null}
      </div>
    );
  },
);
Textarea.displayName = 'Textarea';

export { Textarea };
